import { useEffect, useRef, useState } from 'react';
import { useFirebase } from '../../../contexts/FirebaseContext';
import { hasVerifiedCompanyAccess } from '../../../contexts/free-week.helpers';
import { Footer } from '../../components/Footer/Footer';
import { FullscreenLoader } from '../../components/FullscreenLoader/FullscreenLoader';
import { ScrollableList } from '../../components/ScrollableList/ScrollableList';
import { InfiniteScrollableList } from '../../components/InfiniteScrollableList/InfiniteScrollableList';
import { AppShell } from '../../components/AppShell/AppShell';
import styles from './Browse.module.scss';
import { Badge, Modal } from 'react-bootstrap';
import {
  faCertificate,
  faEllipsisH,
  faExclamation,
  faFilm,
  faLightbulb,
  faMedal,
  faPlay,
  faTimes,
  faTrophy,
  faUserCircle,
  faVideo,
} from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useMobileDetect } from '../../../hooks/useMobileDetect';
import { MaskedIcon } from '../../components/MaskedIcon/MaskedIcon';
import { DeferredRender } from '../../components/DeferredRender/DeferredRender';
import { canBuyEcm } from '../../utils/utils';
import { useTranslation } from 'react-i18next';
import { OOLogo } from '../../components/OOLogo/OOLogo';
import { useHistory, useLocation } from 'react-router-dom';
import { Billboard } from '../../components/Billboard/Billboard';
import { useGaming } from '../../../contexts/GamingContext';
import { useCurrentLanguage } from '../../../hooks/useCurrentLanguage';
import { useOnScreen } from '../../../hooks/useOnScreen';
import { shouldUseSeriesTagsPagination } from '../../utils/loading-strategy';
import { shouldShowBrowseFreeVideos } from './browse-visibility.helpers';

const BrowseComponent = () => {
  const {
    userDataFmt,
    comingSoonVideos,
    mostViewsVideos,
    freeVideos,
    keepWatching,
    newVideos,
    updateUserData,
    billboard,
    videos,
    series,
    seriesAuthors,
    seriesTags,
    hasMoreSeriesTags,
    loadMoreSeriesTags,
    loadingMoreSeriesTags,
    staticData,
    subscription,
    isTrial,
    isFreeWeek,
    recommendations,
    bonusVideos,
    userCompanyData,
  } = useFirebase();
  const { isMobile } = useMobileDetect();
  const { t } = useTranslation();
  const history = useHistory();
  const { hash } = useLocation();
  const { isEnabled: isGamingEnabled } = useGaming();
  const { currentLanguage } = useCurrentLanguage();

  const seriesConfig: { showSeriesInBrowse: boolean; showAuthorSeriesInBrowse: boolean; showTagSeriesInBrowse: boolean } = {
    showSeriesInBrowse: false,
    showAuthorSeriesInBrowse: false,
    showTagSeriesInBrowse: true,
  };

  const billboardWrapperRef = useRef<HTMLDivElement>();
  const seriesTagsSentinelRef = useRef<HTMLDivElement>(null);
  const [hasBillboard, setHasBillboard] = useState(false);
  const [billboardPlay, setBillboardPlay] = useState(false);
  const [bannerShow, setBannerShow] = useState(false);
  const isSeriesTagsSentinelVisible = useOnScreen(seriesTagsSentinelRef, '1200px');
  const shouldUsePagedSeriesTags = shouldUseSeriesTagsPagination(userDataFmt, staticData, seriesConfig.showTagSeriesInBrowse);
  const isAso = userDataFmt?.profile?.formData?.categoriaProfessionale == 'aso';
  const videosById = videos.reduce((acc, video) => {
    acc[video.id] = video;
    return acc;
  }, {} as Record<string, any>);
  const accessibleFreeVideos = freeVideos.filter(
    (video) => !Array.isArray(video.companies) || !video.companies.length || hasVerifiedCompanyAccess(video, userCompanyData?.id)
  );

  useEffect(() => {
    return () => {
      updateUserData(null, { skipListUpdate: true }).catch(console.error);
    };
  }, []);

  useEffect(() => {
    if (!userDataFmt) return;
    setBannerShow(!userDataFmt.hasAlreadySeenBanner);
  }, [userDataFmt]);

  useEffect(() => {
    setHasBillboard(!!billboard);
    setBillboardPlay(!!billboard && !isMobile);
  }, [billboard, isMobile]);

  useEffect(() => {
    if (!shouldUsePagedSeriesTags) return;
    if (!hasMoreSeriesTags) return;
    if (!isSeriesTagsSentinelVisible) return;
    if (loadingMoreSeriesTags) return;

    loadMoreSeriesTags().catch(console.error);
  }, [shouldUsePagedSeriesTags, hasMoreSeriesTags, isSeriesTagsSentinelVisible, loadingMoreSeriesTags, loadMoreSeriesTags]);

  useEffect(() => {
    if (hash) return;
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, [hash]);

  useEffect(() => {
    if (!['#continue-watching', '#top-of-week'].includes(hash)) return;
    const target = document.getElementById(hash.slice(1));
    if (!target) return;

    const topbarHeight = document.querySelector('header')?.getBoundingClientRect().height || 0;
    const top = target.getBoundingClientRect().top + window.scrollY - topbarHeight;
    window.scrollTo({ top, behavior: 'smooth' });
  }, [hash, hasBillboard, keepWatching.length, mostViewsVideos.length, seriesTags?.length]);

  if (!userDataFmt || !seriesTags || !seriesTags.length || (hasBillboard && !billboard)) return <FullscreenLoader />;

  const currentMoment = Date.now();
  const showFreeVideos = shouldShowBrowseFreeVideos({
    isFreeWeek,
    isTrial,
    freeVideos: accessibleFreeVideos,
  });

  return (
    <>
      {
        bannerShow && 
        canBuyEcm(subscription, userDataFmt) && 
        currentLanguage == staticData.BANNER_PROMO_LANG && 
        userDataFmt.profile?.formData?.categoriaProfessionale == staticData.BANNER_PROMO_TARGET && 
        staticData.BANNER_PROMO_START <= currentMoment &&
        staticData.BANNER_PROMO_END >= currentMoment &&
        staticData.BANNER_PROMO_SRC && (
        <Modal
          className={`${styles.bannerPromoModal} dark-modal`}
          size={'lg'}
          centered  
          show={!userDataFmt.hasAlreadySeenBanner}
          onHide={() => {
            setBannerShow(false);
            updateUserData({ hasAlreadySeenBanner: true }).catch(console.error);
          }}
        >
          <Modal.Body>
            <button
              className={`btn btn-close ${styles.promoClose}`}
              onClick={() => {
                setBannerShow(false);
                updateUserData({ hasAlreadySeenBanner: true }).catch(console.error);
              }}
            >
              <FontAwesomeIcon icon={faTimes} />
            </button>
            <div 
            className={'banner-promo'}
              onClick={() => {
                if (canBuyEcm(subscription, userDataFmt)) {
                  setBannerShow(false);
                  updateUserData({ hasAlreadySeenBanner: true }).catch(console.error);
                  history.push('/buy_ecm');
                }
              }}
              style={{ cursor: canBuyEcm(subscription, userDataFmt) ? 'pointer' : 'default' }}
            >
              <img src={staticData.BANNER_PROMO_SRC} alt={staticData.BANNER_PROMO_TARGET} />
            </div>
          </Modal.Body>
        </Modal>
      )}

      <AppShell className={styles.shell}>
       <div className={styles.page}>
        <div className={`${styles.billboardVideo} ${styles.billboardVideoOverlap}`} ref={billboardWrapperRef}>
        {hasBillboard && !isMobile && <Billboard video={billboard} play={billboardPlay} setPlay={setBillboardPlay} wrapper={billboardWrapperRef} />}
        {isMobile && (
          <>
            <div
              className={styles.billboardMobileImage}
              onClick={() => {
                if (!(staticData.SHOW_ADVENT_CALENDAR_2022 && staticData.ADVENT_BACKGROUND && !isAso)) return;
                history.push('/advent');
              }}
              style={{
                backgroundImage: `url(${staticData.SHOW_ADVENT_CALENDAR_2022 && staticData.ADVENT_BACKGROUND && !isAso ? staticData.ADVENT_BACKGROUND : staticData.BACKGROUND
                  })`,
              }}
            />
          </>
        )}
      </div>

       <div className={styles.browseContent}>
        {isGamingEnabled && !!bonusVideos.length && (
           <ScrollableList
             title={t('labels.bonusVideos')}
             elements={bonusVideos}
             seeAllUrl={'/browse'}
            onDetailsOpen={() => {
              if (billboardPlay) setBillboardPlay(false);
            }}
          >
            <FontAwesomeIcon color={'#b7cce0'} icon={faMedal} />
           </ScrollableList>
        )}

        {!!staticData.SHOW_COMING_SOON && !!comingSoonVideos.length && (
           <ScrollableList
             title={
              <span>
                Coming s<OOLogo />n
              </span>
            }
             elements={comingSoonVideos}
             seeAllUrl={'/browse'}
            onDetailsOpen={() => {
              if (billboardPlay) setBillboardPlay(false);
            }}
          >
            <FontAwesomeIcon color={'#b7cce0'} icon={faFilm} />
           </ScrollableList>
        )}

        {!!newVideos.length && (
           <ScrollableList
             title={t('labels.nuoveUscite')}
             forceVertical={true}
             elements={newVideos}
             seeAllUrl={'/browse'}
            onDetailsOpen={() => {
              if (billboardPlay) setBillboardPlay(false);
            }}
          >
            <MaskedIcon bg={faCertificate} bgColor={'#b7cce0'} fg={faExclamation} fgColor={'#141414'} foregroundStyles={{ transform: 'translateX(0.5px)' }} />
           </ScrollableList>
        )}

        {!!keepWatching.length && (
          <div id="continue-watching">
             <InfiniteScrollableList
               title={t('labels.continuaAGuardare')}
              elements={keepWatching}
              keepWatching={true}
              onDetailsOpen={() => {
                if (billboardPlay) setBillboardPlay(false);
              }}
            >
              <MaskedIcon bg={faPlay} bgColor={'#b7cce0'} fg={faEllipsisH} fgColor={'#141414'} foregroundStyles={{ transform: 'translateX(-1px)' }} />
             </InfiniteScrollableList>
          </div>
        )}

        {!isTrial && !!recommendations && !!recommendations.length && (
           <ScrollableList
             title={t('labels.videoConsigliati')}
             elements={recommendations}
             seeAllUrl={'/browse'}
            onDetailsOpen={() => {
              if (billboardPlay) setBillboardPlay(false);
            }}
          >
            <FontAwesomeIcon color={'#b7cce0'} icon={faLightbulb} fixedWidth={true} />
           </ScrollableList>
        )}

         {showFreeVideos && (
            <InfiniteScrollableList
             title={t('labels.videoGratuiti')}
             elements={accessibleFreeVideos}
            onDetailsOpen={() => {
              if (billboardPlay) setBillboardPlay(false);
            }}
          >
            <FontAwesomeIcon color={'#b7cce0'} icon={faVideo} />
           </InfiniteScrollableList>
        )}

        {!!staticData.SHOW_MOST_VIEWS && !!mostViewsVideos.length && (
          <div id="top-of-week">
             <ScrollableList
               title={t('labels.topWeek')}
               forceVertical={true}
               showPosition={true}
               elements={mostViewsVideos}
               seeAllUrl={'/browse'}
              onDetailsOpen={() => {
                if (billboardPlay) setBillboardPlay(false);
              }}
            >
              <FontAwesomeIcon color={'#b7cce0'} icon={faTrophy} />
             </ScrollableList>
          </div>
        )}

        {seriesConfig.showSeriesInBrowse &&
          !!series?.length &&
          series.map((serie, index) => (
            <DeferredRender key={serie.id} timeout={index * 500}>
               <ScrollableList
                 title={serie.name}
                 description={serie.description}
                 elements={serie.seasons[0].episodes.map((ep) => videosById[typeof ep.video === 'string' ? ep.video : ep.video?.id]).filter(Boolean)}
                 seeAllUrl={'/categorie'}
                onDetailsOpen={() => {
                  if (billboardPlay) setBillboardPlay(false);
                }}
              >
                {!isMobile && <Badge variant={'dark'}>{t('labels.serie')}</Badge>}
              </ScrollableList>
            </DeferredRender>
          ))}

        {seriesConfig.showTagSeriesInBrowse &&
          !!seriesTags?.length &&
          seriesTags.map((serie, index) => (
            <DeferredRender key={serie.tag.id} timeout={index + 1}>
               <ScrollableList
                 tagId={serie.tag.id}
                 title={serie.tag.label}
                 elements={serie.tagVideos}
                 seeAllUrl={`/categorie/${serie.tag.id}`}
                onDetailsOpen={() => {
                  if (billboardPlay) setBillboardPlay(false);
                }}
              />
            </DeferredRender>
          ))}

        {shouldUsePagedSeriesTags && <div ref={seriesTagsSentinelRef} className={styles.seriesTagsSentinel} />}

        {shouldUsePagedSeriesTags && loadingMoreSeriesTags && <FullscreenLoader fullscreen={false} />}

        {seriesConfig.showAuthorSeriesInBrowse &&
          !!seriesAuthors?.length &&
          seriesAuthors.map((serie, index) => (
            <DeferredRender key={serie.author.id} timeout={index * 500}>
               <ScrollableList
                 title={serie.author.name}
                 elements={serie.authorVideos}
                 seeAllUrl={`/tootors/${serie.author.id}`}
                onDetailsOpen={() => {
                  if (billboardPlay) setBillboardPlay(false);
                }}
              >
                {!!serie.author.photo ? (
                   <img src={serie.author.photo} alt={serie.author.name} className={styles.authorPhoto} />
                ) : (
                  <MaskedIcon bg={faUserCircle} bgColor={'#fafafa'} />
                )}
              </ScrollableList>
            </DeferredRender>
          ))}
      </div>

       <div className={styles.footer}>
         <Footer />
       </div>
       </div>
      </AppShell>
    </>
  );
};

export default BrowseComponent;
